import { NETWORK_RETRY_BASE_MS, waitForConnectivity } from "@/lib/api";

/**
 * Shared primitives for surviving a flaky auth request.
 *
 * The signup modal used to treat one failed `signUp()` as a dead end: it fired
 * a single immediate `signInWithPassword()` into the same dead connection and,
 * when that also failed, told the user we couldn't reach our servers — without
 * ever establishing whether the account had in fact been created. On mobile
 * Safari the two requests share the same blip, so the retry almost never
 * changed the outcome (PANDA-signupFalseServerError).
 *
 * These helpers live outside the component so the sibling auth surfaces
 * (login modal, update-password) can adopt the same policy without a second
 * copy of the error-shape list.
 */

/**
 * A failed network request to Supabase surfaces as a transient error:
 * Safari reports "Load failed", Chrome "Failed to fetch", and Supabase's
 * auth client wraps both as AuthRetryableFetchError. These are NOT real
 * signup failures — the request often still reaches Supabase and creates
 * the account, the client just never reads the response. They must never
 * be shown raw (the message leaks the Supabase hostname to the user).
 *
 * Moved verbatim from components/auth/signup-modal.tsx.
 */
export function isTransientAuthError(
  error: { name?: string; message?: string } | null | undefined,
): boolean {
  if (!error) return false;
  if (error.name === "AuthRetryableFetchError") return true;
  const msg = (error.message || "").toLowerCase();
  return (
    msg.includes("load failed") ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network error")
  );
}

/** HTTP status off a Supabase AuthApiError, when it carries one. */
export function authErrorStatus(
  error: { status?: number } | null | undefined,
): number | null {
  return typeof error?.status === "number" ? error.status : null;
}

/**
 * True only when the browser itself reports no route out.
 *
 * Mirrors the classifyBuildError policy (lib/api.ts): the same connection-level
 * failure is raised when the request dies at the server while the client stays
 * perfectly online, so a fetch failure alone must never be used to blame the
 * user's connection.
 */
export function isBrowserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Bound how long the UI waits on a promise.
 *
 * The underlying socket may outlive the race — there is no fetch override on
 * lib/supabase/client.ts — but the user never stares at a spinner past `ms`.
 */
export async function withTimeout<T>(
  promise: PromiseLike<T>,
  ms: number,
  onTimeout: () => T,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(onTimeout()), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** What the server could tell us about an address after a failed attempt. */
export type AccountProbe = "exists" | "absent" | "unknown";

/**
 * Server-authoritative "does this account exist right now?".
 *
 * This is the only sound way to answer the question after a lost response.
 * A local `getSession()`/`getUser()` read cannot help: when `signUp()`'s
 * response never arrives the client persists nothing, so a local read can only
 * ever surface a session that already existed before this attempt.
 *
 * ONLY `checked === true` is trusted. A 429, a 5xx, a missing service-role key
 * and a thrown fetch all resolve to "unknown", so a rate-limited probe can
 * never be misread as "no account exists".
 */
export async function probeAccountExists(
  email: string,
  timeoutMs = 5_000,
): Promise<AccountProbe> {
  try {
    const res = await withTimeout(
      fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }),
      timeoutMs,
      () => null,
    );
    if (!res || !res.ok) return "unknown";
    const body = (await res.json()) as { exists?: boolean; checked?: boolean };
    if (body?.checked !== true) return "unknown";
    return body.exists === true ? "exists" : "absent";
  } catch {
    return "unknown";
  }
}

/** Total wall-clock the recovery loop may spend before it gives up. */
export const RECOVERY_BUDGET_MS = 15_000;
/** Per sign-in attempt inside the recovery loop. */
export const RECOVERY_ATTEMPT_TIMEOUT_MS = 6_000;
/**
 * Bound on signUp itself. Without this the call rides Safari's ~60s ceiling
 * with no client-side limit; the repo's own precedent for a user-facing
 * network wait is 45s (lib/api.ts CREATE_TIMEOUT_MS).
 */
export const SIGNUP_REQUEST_TIMEOUT_MS = 20_000;
/** How long to wait for the radio/tab to come back before retrying anyway. */
export const CONNECTIVITY_WAIT_MS = 3_000;

export { NETWORK_RETRY_BASE_MS, waitForConnectivity };
